import { Button, ButtonVariant, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { UseMutationResult } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppNotification } from '@feature/utils';

interface TravelRequestConfirmActionButtonProps {
  label: string;
  confirmTitle: string;
  confirmContent: string;
  color?: string;
  // Styles the opening button only; the dialog's confirm button stays filled.
  variant?: ButtonVariant;
  disabled?: boolean;
  mutation: UseMutationResult<unknown, Error, void>;
}

export function TravelRequestConfirmActionButton(props: TravelRequestConfirmActionButtonProps) {
  const { label, confirmTitle, confirmContent, color, variant, disabled, mutation } = props;
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [opened, { open, close }] = useDisclosure(false);

  // Two clicks in one frame both see isPending false, so the loading button alone lets the second one through.
  const inFlight = useRef(false);

  const onConfirm = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutation.mutate(undefined, {
      onError: error => {
        notification.error(error);
        close();
      },
      onSuccess: () => {
        notification.success();
        close();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <>
      <Button
        size="xs"
        color={color}
        variant={variant}
        disabled={disabled}
        onClick={open}
      >
        {label}
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={confirmTitle}
        centered
      >
        <Stack>
          <Text
            style={{ textAlign: 'center' }}
            fw="500"
          >
            {confirmContent}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={close}
              loading={mutation.isPending}
            >
              {t('button.cancel')}
            </Button>
            <Button
              onClick={onConfirm}
              color={color}
              loading={mutation.isPending}
            >
              {label}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
